/**
 * DEFCAT-R2-17 — pick a `LocalizedString` value for the current UI language,
 * with an English fallback (then first-available, then empty).
 *
 * The app has no i18n locale store yet — EVERYTHING renders English (`locText`
 * in apps/web does the same). The first cut keyed "current UI language" off
 * `navigator.language`, which made drawer placeholders/labels render German on
 * a de-* browser inside an otherwise English UI (Christian, 2026-06-04 — same
 * finding as the L0-names fix in apps/web). Do NOT key display language off
 * the browser; when a real locale store lands (driven by the account-settings
 * preferred language), switch `currentUiLanguage()` + apps/web `locText`
 * together. `pickLocalized(s, language)` stays language-parameterized so that
 * switch is one-line.
 */
import type { LocalizedString } from "@aeriosiq/types";

/** Base language subtag from a BCP-47 tag, lowercased (e.g. "de-AT" → "de"). */
export function baseLanguage(tag: string | null | undefined): string {
  if (!tag) return "en";
  const base = tag.split("-")[0]?.trim().toLowerCase();
  return base && base.length > 0 ? base : "en";
}

/** The app's current UI language. English until a locale store exists — see
 *  the module doc; deliberately NOT navigator.language. */
export function currentUiLanguage(): string {
  return "en";
}

/**
 * Resolve a localized value: current language → `en` → first non-empty → "".
 * A null/undefined input or an all-empty map yields "".
 */
export function pickLocalized(
  s: LocalizedString | Record<string, string> | null | undefined,
  language: string = currentUiLanguage(),
): string {
  if (!s) return "";
  const map = s as Record<string, string | undefined>;
  const lang = map[language];
  if (typeof lang === "string" && lang.trim().length > 0) return lang;
  const en = map.en;
  if (typeof en === "string" && en.trim().length > 0) return en;
  for (const key of Object.keys(map)) {
    const v = map[key];
    if (typeof v === "string" && v.trim().length > 0) return v;
  }
  return "";
}
